import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Button, Grid, InputAdornment, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { TopicType } from 'constants/enums/TopicTypeEnum'

import { BaseTextField } from 'components/Form'
import TopicChips from 'components/Hero/TopicChips'
import WarningMessage from 'components/WarningMessage/WarningMessage'

import { typography } from 'theme/typography'

import { commonStyles } from 'styles/common.styles'

import { styles } from './Section1.styles'

export const TEST_ID = 'musical-chairs-section1'

export type Props = {
	companyProblem: string
	createPrompt: () => void
	editable: boolean
	generateScenarioDisabled: () => boolean
	innovationCompany: string
	innovationTopic: TopicType
	isLoading: boolean
	resetAllField: () => void
	setCompanyProblem: (val: string) => void
	setInnovationCompany: (val: string) => void
	setInnovationTopic: (val: TopicType) => void
} & React.HTMLAttributes<HTMLDivElement>

const Section1 = ({
	innovationCompany,
	setInnovationCompany,
	innovationTopic,
	setInnovationTopic,
	companyProblem,
	setCompanyProblem,
	generateScenarioDisabled,
	resetAllField,
	isLoading,
	createPrompt,
	editable,
	...props
}: Props) => {
	React.useEffect(() => {
		const storedCompany = localStorage.getItem('innovationCompany')
		const storedTopic = localStorage.getItem('innovationTopic')
		const storedProblem = localStorage.getItem('businessProcess')
		if (storedCompany) setInnovationCompany(storedCompany)
		if (storedTopic) setInnovationTopic(storedTopic as TopicType)
		if (storedProblem) setCompanyProblem(storedProblem)
	}, [setInnovationCompany, setInnovationTopic, setCompanyProblem])
	React.useEffect(() => {
		const debounce = setTimeout(() => {
			localStorage.setItem('innovationCompany', innovationCompany)
			localStorage.setItem('innovationTopic', innovationTopic)
			localStorage.setItem('businessProcess', companyProblem)
		}, 200)
		return () => clearTimeout(debounce)
	}, [innovationCompany, innovationTopic, companyProblem])
	return (
		<Grid container {...props}>
			<Grid item xs={12}>
				<Typography sx={commonStyles.header} variant={'overline'}>
					1. INNOVATION TOPIC
				</Typography>
			</Grid>
			<Grid container>
				<Grid item sx={styles.sideBar} />
				<Grid
					item
					xs
					container
					direction="column"
					sx={styles.contentContainer}
				>
					<Grid item>
						<Typography
							data-testid={`${TEST_ID}__company-question`}
							sx={styles.textBlack}
						>
							<span
								data-testid={`${TEST_ID}__company-question-text`}
							>
								What company are you brainstorming about today?
							</span>{' '}
							<Box component="span" sx={typography.asterisk}>
								*
							</Box>
						</Typography>
					</Grid>
					<Grid item>
						<BaseTextField
							disabled={!editable}
							InputLabelProps={{
								style: styles.inputLabel,
								required: false,
							}}
							variant="outlined"
							data-testid={`${TEST_ID}__company-name-input`}
							label="Enter company name here."
							value={innovationCompany}
							onChangeValue={(val) => setInnovationCompany(val)}
							required
							size="medium"
							InputProps={{
								endAdornment: !editable && (
									<InputAdornment position="start">
										<LockIcon />
									</InputAdornment>
								),
							}}
							sx={styles.textField}
						/>
					</Grid>
					<Grid item>
						<Typography
							data-testid={`${TEST_ID}__topic-question`}
							sx={styles.textBlack}
						>
							What business topic do you want to focus on?{' '}
							<Box component="span" sx={typography.asterisk}>
								*
							</Box>
						</Typography>
					</Grid>
					<Grid item>
						<TopicChips
							color={styles.chip.color}
							innovationTopic={innovationTopic}
							setInnovationTopic={setInnovationTopic}
							editable={editable}
						/>
					</Grid>
					<Grid item>
						<Typography
							data-testid={`${TEST_ID}__specific-question`}
							sx={{
								...styles.textBlack,
								...commonStyles.textOpaque,
							}}
						>
							Name a high-level{' '}
							<Box component="span" sx={styles.businessProcess}>
								business process
							</Box>{' '}
							you want to innovate (within 5-21 words).{' '}
							<Box component="span" sx={typography.asterisk}>
								*
							</Box>
						</Typography>
					</Grid>
					<Grid
						data-testid={`${TEST_ID}__company-problem-container`}
						item
					>
						<BaseTextField
							disabled={!editable}
							InputLabelProps={{
								style: styles.inputLabel,
								required: false,
							}}
							variant="outlined"
							data-testid={`${TEST_ID}__company-problem-input`}
							label="Summarize in 5-21 words"
							onChangeValue={(val) => setCompanyProblem(val)}
							value={companyProblem}
							size="medium"
							required
							InputProps={{
								endAdornment: !editable && (
									<InputAdornment position="start">
										<LockIcon />
									</InputAdornment>
								),
							}}
							sx={styles.textField}
						/>
					</Grid>
					<WarningMessage
						prompt={companyProblem}
						color="gameMusicalChairs"
					/>
					<Grid
						item
						container
						spacing={4}
						sx={styles.buttonContainer}
					>
						<Grid item>
							<Button
								color="secondary"
								variant="contained"
								data-testid={`${TEST_ID}__create-prompt-button`}
								disabled={
									generateScenarioDisabled() || isLoading
								}
								onClick={createPrompt}
							>
								START IDEATING
							</Button>
						</Grid>
						<Grid item>
							<Button
								disabled={!editable}
								variant="text"
								color="secondary"
								data-testid={`${TEST_ID}__reset-all-button`}
								onClick={resetAllField}
							>
								RESET ALL FIELDS
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default Section1
